import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-[color:var(--navy)] mb-2">Terms and Conditions of Service</h1>
            <p className="text-slate-500">Last Updated: April 18, 2026</p>
          </div>

          <div className="space-y-6 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">1. AGREEMENT TO TERMS</h2>
              <p>
                These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("customer," "you") and Pasco Appliance LLC ("we," "us," or "our"), concerning your access to and use of our appliance repair services. By scheduling a service appointment, you agree to be bound by these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">2. SERVICE CALL & DIAGNOSTIC FEE</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Base Fee:</strong> We charge a flat, non-refundable $100.00 Diagnostic/Service Call Fee for the technician to travel to your location and diagnose the appliance.</li>
                <li><strong>Application to Repair:</strong> If you choose to proceed with the recommended repair, this $100.00 fee will be applied toward the total cost of the final repair (parts and labor).</li>
                <li><strong>Refusal of Repair:</strong> If you decide NOT to proceed with the repair after the diagnosis is made, or if the appliance is deemed unrepairable, the $100.00 fee remains due and payable immediately.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">3. PAYMENT TERMS</h2>
              <p>
                Payment in full is due immediately upon completion of the repair or upon completion of the diagnosis if no repair is performed. We accept major credit cards and digital payments (via Square). We do not offer billing or Net-30 terms for residential customers. Unpaid balances will be subject to a late fee of 1.5% per month and may be sent to collections.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">4. LIMITATION OF LIABILITY (CRITICAL PROTECTIONS)</h2>
              <p className="mb-3">
                Appliance repair inherently involves working with machines connected to household plumbing and electrical systems. Pasco Appliance LLC is not liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Pre-existing Conditions:</strong> Damage caused by pre-existing compromised plumbing, brittle or degraded plastic valves, or faulty electrical wiring.</li>
                <li><strong>Water Damage:</strong> We are not responsible for water damage caused by the failure of old shut-off valves, hoses, or plumbing fixtures that break during the normal course of servicing an appliance.</li>
                <li><strong>Food Loss:</strong> Under no circumstances is Pasco Appliance LLC responsible for the loss of food, medicine, or other perishable items due to the failure of a refrigerator or freezer, or due to delays in obtaining replacement parts.</li>
                <li><strong>Property Damage Limit:</strong> Our total liability for any property damage arising from our services is strictly limited to the total amount paid by you for the specific repair service in question.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">5. WORK SITE CONDITIONS</h2>
              <p>
                The customer must ensure that the appliance is reasonably accessible. We are not responsible for moving heavy furniture, modifying cabinetry, or repairing flooring to access the appliance. If the appliance has a pest infestation (e.g., roaches, rodents), the technician reserves the right to refuse service, and the $100.00 diagnostic fee will still apply.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">6. PARTS AND LABOR WARRANTY</h2>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li><strong>Labor Warranty:</strong> We provide a 30-day warranty on all labor performed by our technicians. If the exact same issue reoccurs within 30 days due to a failure in our workmanship, we will return and fix it at no additional labor charge.</li>
                <li><strong>Parts Warranty:</strong> We provide a 90-day warranty on all parts supplied and installed by Pasco Appliance LLC.</li>
              </ul>
              <p className="font-semibold mb-2">Exclusions: This warranty does NOT cover:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Parts purchased or supplied by the customer (we do not guarantee aftermarket or customer-supplied parts).</li>
                <li>Issues unrelated to the original repair.</li>
                <li>Clogs (e.g., drain pumps, dryer vents, defrost drains), as these are maintenance issues, not mechanical failures.</li>
                <li>Damage caused by user negligence, power surges, or Acts of God.</li>
                <li>Any appliance that has been tampered with or serviced by another company or the homeowner after our initial repair.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">7. CANCELLATION AND NO-SHOW POLICY</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>We require at least 4 hours' notice for appointment cancellations.</li>
                <li>If our technician arrives at the scheduled location and no one over the age of 18 is present to grant access, or if the appointment is canceled at the door, the $100.00 Diagnostic Fee will be charged to the card on file.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">8. CHARGEBACK POLICY (SQUARE PAYMENTS)</h2>
              <p className="mb-3">
                By authorizing payment via credit card or digital wallet (including Square), you agree that you will not dispute the charge with your bank or credit card company for valid services rendered.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The $100.00 Diagnostic Fee is strictly non-refundable once the technician arrives and performs the diagnostic service, regardless of whether you choose to proceed with the repair.</li>
                <li>Fraudulent chargebacks will be aggressively disputed using GPS tracking logs, signed invoices, and text message communication logs. We reserve the right to send accounts with fraudulent chargebacks to a third-party collection agency and pursue legal action in Pasco County courts.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">9. GOVERNING LAW</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in Pasco County, Florida.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Pasco Appliance LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
